import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { DashShell, Badge } from "@/components/dash/DashShell";
import { ADMIN_NAV } from "./nav";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, inr, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statuses, setStatuses] = useState([]);
    const [bdes, setBdes] = useState([]);
    const [active, setActive] = useState(null);
    const [filter, setFilter] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/orders", { params: filter ? { payment_status: filter } : {} }).then(({ data }) => setOrders(data)).finally(() => setLoading(false));
    }, [filter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/meta").then(({ data }) => setStatuses(data.project_stages));
        api.get("/admin/bdes").then(({ data }) => setBdes(data));
    }, []);

    const patch = async (orderId, body) => {
        try { await api.patch(`/admin/orders/${orderId}`, body); toast.success("Updated"); load(); setActive((a) => a ? { ...a, ...body } : a); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <DashShell nav={ADMIN_NAV} title="Order Management">
            <div className="mb-4 flex gap-2">
                {["", "Paid", "Pending", "Failed"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-full text-sm font-medium ${filter === s ? "bg-teal-900 text-cream" : "bg-white border border-black/5 text-teal-900/70"}`} data-testid={`order-filter-${s || "all"}`}>{s || "All"}</button>
                ))}
            </div>
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-x-auto">
                {loading ? <div className="grid place-items-center py-24 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
                    <table className="w-full min-w-[820px] text-sm" data-testid="orders-table">
                        <thead className="bg-teal-900/5 text-teal-900/60 text-xs uppercase">
                            <tr>{["Order ID", "Customer", "Service/Package", "Amount", "GST", "Payment", "Order Status", "BDE", "Date"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {orders.map((o) => (
                                <tr key={o.order_id} onClick={() => setActive(o)} className="hover:bg-black/[0.02] cursor-pointer" data-testid={`order-row-${o.order_id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-teal-700">{o.order_id}</td>
                                    <td className="px-4 py-3 font-medium text-teal-900">{o.customer_name}</td>
                                    <td className="px-4 py-3 text-teal-900/70 max-w-[180px] truncate">{[o.package, o.service].filter(Boolean).join(", ")}</td>
                                    <td className="px-4 py-3 font-mono">{inr(o.amount)}</td>
                                    <td className="px-4 py-3 font-mono text-teal-900/60">{inr(o.gst)}</td>
                                    <td className="px-4 py-3"><Badge status={o.payment_status} /></td>
                                    <td className="px-4 py-3 text-teal-900/70">{o.order_status}</td>
                                    <td className="px-4 py-3 text-teal-900/70">{o.assigned_bde_name || "—"}</td>
                                    <td className="px-4 py-3 text-teal-900/50 whitespace-nowrap">{(o.created_at || "").slice(0, 10)}</td>
                                </tr>
                            ))}
                            {!orders.length && <tr><td colSpan={9} className="text-center py-16 text-teal-900/40">No orders found</td></tr>}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="max-w-lg">
                    {active && (
                        <>
                            <DialogHeader><DialogTitle className="font-serif text-xl">Order {active.order_id}</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <Info k="Customer" v={active.customer_name} /><Info k="Business" v={active.business_name || "—"} />
                                    <Info k="Mobile" v={active.mobile} /><Info k="Email" v={active.email} />
                                    <Info k="Amount" v={inr(active.amount)} /><Info k="Payment Ref" v={active.payment_reference || "—"} />
                                </div>
                                <div>
                                    <label className="text-xs text-teal-900/60">Order Status</label>
                                    <select className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm mt-1" value={active.order_status} onChange={(e) => patch(active.order_id, { order_status: e.target.value })} data-testid="order-status-select">
                                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-teal-900/60">Payment Status</label>
                                    <select className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm mt-1" value={active.payment_status} onChange={(e) => patch(active.order_id, { payment_status: e.target.value })} data-testid="order-payment-select">
                                        {["Pending", "Paid", "Failed"].map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-teal-900/60">Assign BDE</label>
                                    <select className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm mt-1" value={active.assigned_bde_id || ""} onChange={(e) => patch(active.order_id, { assigned_bde_id: e.target.value })} data-testid="order-bde-select">
                                        <option value="">Unassigned</option>{bdes.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </DashShell>
    );
}
function Info({ k, v }) { return <div><div className="text-xs text-teal-900/50 uppercase">{k}</div><div className="text-teal-900 mt-0.5 break-words">{v}</div></div>; }
